import { supabase } from '../../lib/supabase';
import type { AssistantEvent, PropostaItem } from '../../../supabase/functions/assistente/sse.ts';
import { readSse } from './sseReader';

export type PropostaEstado = 'pendente' | 'enviando' | 'registrado' | 'cancelado' | 'erro';
export type SimulacaoEvento = Extract<AssistantEvent, { type: 'simulacao' }>;
export interface ChatItem {
  role: 'user' | 'assistant';
  text: string;
  proposta?: PropostaItem[];
  propostaEstado?: PropostaEstado;
  propostaMensagem?: string;
  simulacao?: { nome: string; por_cargo: SimulacaoEvento['por_cargo'] };
}

export class AssistantError extends Error {
  constructor(public codigo: string, message: string) { super(message); }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

// Texto visível de uma mensagem gravada: pula o bloco <contexto>, thinking e ferramentas.
function visibleText(content: Array<{ type: string; text?: string }>): string {
  return content.filter((b) => b.type === 'text' && b.text && !b.text.startsWith('<contexto>'))
    .map((b) => b.text).join('');
}

export const assistantService = {
  async listConversations(orgId: string) {
    const { data, error } = await db.from('ai_conversations')
      .select('id, titulo, updated_at, ai_messages!inner(id)')
      .eq('organization_id', orgId).order('updated_at', { ascending: false }).limit(20);
    if (error) throw error;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (data ?? []).map((c: any) => ({ id: c.id, titulo: c.titulo, updated_at: c.updated_at }));
  },

  async loadMessages(conversationId: string): Promise<ChatItem[]> {
    const { data, error } = await db.from('ai_messages').select('role, content, is_question')
      .eq('conversation_id', conversationId).order('seq', { ascending: true });
    if (error) throw error;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (data ?? []).map((m: any) => ({ role: m.role, text: visibleText(m.content) }))
      .filter((m: ChatItem) => m.text);
  },

  async confirmarLancamentos(orgId: string, itens: PropostaItem[]): Promise<{ criados: number; total_horas: number }> {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/assistente`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session?.access_token ?? ''}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        organization_id: orgId,
        acao: 'confirmar_lancamentos',
        lancamentos: itens.map((l) => ({
          data: l.data, inicio: l.inicio, fim: l.fim, projeto_id: l.projeto_id, descricao: l.descricao,
          ...(l.tarefa_id ? { tarefa_id: l.tarefa_id } : {}),
        })),
      }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || !body.ok) throw new AssistantError(body.codigo ?? 'falha', body.erro ?? 'Não consegui registrar os lançamentos.');
    return { criados: body.criados, total_horas: body.total_horas };
  },

  async ask(
    p: { organizationId: string; conversationId?: string; mensagem: string; section: string },
    onEvent: (e: AssistantEvent) => void,
  ): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/assistente`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session?.access_token ?? ''}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        organization_id: p.organizationId, conversation_id: p.conversationId, mensagem: p.mensagem, section: p.section,
      }),
    });
    if (!res.ok || !res.body) {
      const body = await res.json().catch(() => ({}));
      throw new AssistantError(body.codigo ?? 'falha', body.erro ?? 'Não consegui responder agora.');
    }
    await readSse(res.body, onEvent);
  },
};
